import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Box, Typography, Link, Snackbar, Alert, Select, MenuItem, Tooltip } from '@mui/material';
import { useDispatch } from "react-redux";
import { useGuestUser } from '../../contexts/GuestUserContext';
import LinkIcon from '@mui/icons-material/Link';
import { getAllVariableSets } from 'redux-store/variableSetReducers/variableSetActions';
import { useSelector } from 'react-redux';
import { LOCAL_STORAGE_KEY_FOR_ENVIRONMENT } from 'config/constants';
import { Copy, Settings } from 'lucide-react';
import { checkExtensionInstalled } from 'utils/extensionUtils';
import AgentSelectionDialog from '../Common/AgentSelectionDialog';

const ApiOptions = ({ state, selectedIcon, api, onSelectEnvironmentId, entity, sendClicked, selectedTab }) => {
  const { isGuestUser } = useGuestUser();
  const [selectedOption, setSelectedOption] = useState("option2");
  const dispatch = useDispatch();
  const [shouldShowSnackBar, setShouldShowSnackBar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
  const [showNotificationBlink, setShowNotificationBlink] = useState(false);
  const [prevSendClicked, setPrevSendClicked] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const project = useSelector((state) => state.common.selectedProject);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('cloudAgent');
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const entityResponseStatusCode = Number(entity?.request?.response?.status)
  const isSuccessStatusCode = entityResponseStatusCode >= 200 && entityResponseStatusCode < 300;
  const titleParts = [];
  if (state?.collection?.name) titleParts.push(state.collection.name);
  if (state?.folder) titleParts.push(state.folder.name);
  if (state?.collectionApi) titleParts.push(state.collectionApi.name);
  if (state?.folderApi) titleParts.push(state.folderApi.name);

  const handleOptionChange = (option) => {
    setSelectedOption(option);
    selectedIcon(option);
  };

  const handleEnvironmentChange = (event) => {
    setSelectedEnvironmentId(event.target.value);
    onSelectEnvironmentId(event.target.value);
    localStorage.setItem(LOCAL_STORAGE_KEY_FOR_ENVIRONMENT, event.target.value);
  };

  const tabs = [
    { value: "option1", label: "Overview", disabled: false },
    { value: "option2", label: "Request", disabled: false },
    { value: "option4", label: "AI Tests", disabled: false },
    // { value: "option3", label: "Collected Samples", disabled: false }
  ];

  const handleCloseSnackbar = () => {
    setShouldShowSnackBar(false);
  };

  const onLinkClick = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(window.location.href);
    setShouldShowSnackBar(true);
    setSnackbarMessage('Link copied to clipboard');
  };

  const copyAsCurl = (e, entity) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(entity?.request.curl);
    setShouldShowSnackBar(true);
    setSnackbarMessage("Curl Copied to clipboard");
  }

  useEffect(() => {
    const check = async () => {
      if (!localStorage.getItem("runOn")) {
        localStorage.setItem("runOn", "cloudAgent");
      }
      const installed = await checkExtensionInstalled();
      if (!installed) {
        localStorage.setItem("runOn", "cloudAgent");
      }
      setSelectedAgent(localStorage.getItem("runOn"));
      setExtensionInstalled(installed);
    }
    check();
  }, []);

  // Handle notification blink logic
  useEffect(() => {
    // Detect when sendClicked changes from true to false (response received)
    if (prevSendClicked && !sendClicked) {
      setShowNotificationBlink(true);
    }
    setPrevSendClicked(sendClicked);

    // Hide notification when AI Test tab is selected
    if (selectedTab === 'option4') {
      setShowNotificationBlink(false);
    }
  }, [sendClicked, selectedTab, prevSendClicked]);

  const handleSettingsClick = (e) => {
    e.preventDefault();
    setSettingsDialogOpen(true);
  };

  const handleAgentSave = (agentValue) => {
    setSelectedAgent(agentValue);
    setSettingsDialogOpen(false);
    setShouldShowSnackBar(true);
    setSnackbarMessage(`${agentValue === 'cloudAgent' ? 'Cloud Agent' : 'Local Agent'} selected`);
    localStorage.setItem('runOn', agentValue);
  };

  const handleAgentCancel = () => {
    setSettingsDialogOpen(false);
  };

  useEffect(() => {
    const fetchEnvironments = async () => {
      try {
        const projectId = project.id;
        if (projectId) {
          const result = await dispatch(getAllVariableSets({
            projectId: projectId
          })).unwrap();
          if (result?.variable_sets) {
            setEnvironments(result.variable_sets);
            const savedEnvironmentId = localStorage.getItem(LOCAL_STORAGE_KEY_FOR_ENVIRONMENT) || project.primary_environment_id;
            if (savedEnvironmentId && result.variable_sets.some(env => env.id === savedEnvironmentId)) {
              setSelectedEnvironmentId(savedEnvironmentId);
              onSelectEnvironmentId(savedEnvironmentId);
            } else if (result.variable_sets.length > 0) {
              // Fallback to first environment if no saved environment exists
              const defaultEnv = result.variable_sets.find(env => env.title === 'default') || result.variable_sets[0];
              setSelectedEnvironmentId(defaultEnv.id);
              onSelectEnvironmentId(defaultEnv.id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching environments:', error);
      }
    };


    fetchEnvironments();

  }, [project.id]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingRight: '10px', alignItems: 'center', marginTop: '2px', backgroundColor: 'var(--background-primary)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tabs
            className='tab-style'
            value={selectedOption}
            onChange={(event, newValue) => handleOptionChange(newValue)}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              marginLeft: '10px',
              minHeight: '28px',
              height: '28px',
              border: "1px solid var(--borderColor)",
              borderRadius: "8px",
            }}
          >
            {tabs.map(tab => (
              <Tab
                disabled={isGuestUser && tab.value === "option3"}
                key={tab.value}
                value={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.label}
                    {tab.value === "option4" && showNotificationBlink && isSuccessStatusCode && (
                      <div class="pulse-container">
                        <div class="dot"></div>
                        <div class="pulse"></div>
                        <div class="pulse delay"></div>
                      </div>
                    )}
                  </Box>
                }
                sx={{
                  minHeight: '28px',
                  height: '28px',
                  textTransform: 'none',
                  color: selectedOption === tab.value ? "white !important" : "var(--text-secondary)",
                  backgroundColor: selectedOption === tab.value ? 'var(--primary-color)' : 'var(--background-layout)',
                  fontSize: "12px",
                  fontWeight: selectedOption === tab.value ? "500" : "400",
                  "&:hover": {
                    backgroundColor: 'var(--hover-bg)',
                    color: "var(--text-primary)"
                  },
                  "&.Mui-selected": {
                    color: "white",
                    backgroundColor: "var(--primary-color)", // if you also want to change background
                  }
                }}
              />
            ))}
          </Tabs>

          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0px',
              fontSize: '10px',
              fontWeight: '300',
            }}
          >
            {titleParts.map((part, index) => (
              <span key={index} style={{ fontSize: 12, fontWeight: index === titleParts.length - 1 ? '600' : '500', marginLeft: '5px' }}>
                {index > 0 && ' / '}
                {part}
              </span>
            ))}
            <Link
              href="#"
              onClick={onLinkClick}
            >
              <LinkIcon sx={{ fontSize: '1.4rem', verticalAlign: 'middle', marginLeft: '8px' }} />
            </Link>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Copy as CURL" placement="bottom">
            <button
              onClick={(e) => copyAsCurl(e, entity)}
              className='bg-transparent border-none'
            >
              <Copy color='var(--text-primary)' size={16} />
            </button>
          </Tooltip>
          <Tooltip title={`${localStorage.getItem("runOn") === "cloudAgent" ? "Selected: Cloud Agent" : "Selected: Local Agent"}`} placement="bottom">
            <button
              onClick={handleSettingsClick}
              className='bg-transparent border !border-[var(--borderColor)] !rounded-md py-1 h-[28px] text-[var(--text-primary)] flex items-center gap-1 text-xs'
            >
              <span><Settings size={16} /></span>
              <span className='fs12'> {localStorage.getItem("runOn") === "cloudAgent" ? "Cloud Agent" : "Local Agent"}</span>
            </button>
          </Tooltip>
          <Select
            value={selectedEnvironmentId}
            onChange={handleEnvironmentChange}
            size="small"
            className='!rounded-md'
            displayEmpty
            sx={{
              height: '28px',
              '& .MuiSelect-select': {
                fontSize: '12px',
                padding: '4px 8px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--borderColor)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--borderColor)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--borderColor)',
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '10px' }}>
              Select Environment
            </MenuItem>
            {environments && environments.map((env) => (
              <MenuItem
                key={env.id}
                value={env.id}
                sx={{ fontSize: '10px' }}
              >
                {env.title}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Snackbar
        open={shouldShowSnackBar}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={5000}
      >
        <Alert onClose={handleCloseSnackbar} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <AgentSelectionDialog
        open={settingsDialogOpen}
        onClose={handleAgentCancel}
        onSave={handleAgentSave}
        selectedAgent={selectedAgent}
        extensionInstalled={extensionInstalled}
      />
    </>
  );
};

export default ApiOptions;